/**
 * AARTECH — Quote List (multi-product quotation workflow) v4
 * Client-side, localStorage-backed. No app, no backend required.
 *
 * v4 fix (CRITICAL - root cause of "Quote List page shows nothing"):
 * This script loads with <script defer> in layout/theme.liquid. Per
 * spec, a deferred script executes only AFTER the whole document has
 * been parsed - but inline <script> blocks inside body sections (like
 * sections/aartech-quote-form.liquid's initialization script) run
 * SYNCHRONOUSLY, immediately, as the parser reaches them - which is
 * BEFORE this deferred script has executed. Every consumer's
 * "if (!window.AartechQuoteList) return;" guard was therefore silently
 * aborting on every page load: the header count still worked (it's
 * driven by this script's own DOMContentLoaded listener, which fires
 * after this script has loaded), but the Quote List panel's render()
 * and all its event listeners were NEVER wired up, because the
 * consuming script gave up before this script ever ran.
 *
 * Fix: this script now dispatches a 'aartech:quote-list-ready' event
 * immediately after window.AartechQuoteList is defined. Every consumer
 * (quote-form, purchase-actions, card-gallery) now waits for that event
 * if AartechQuoteList isn't already present, instead of bailing out.
 * This is correct regardless of script load order, defer/async timing,
 * or Shopify section reloads.
 *
 * v3 additions (UX redesign pass, unchanged):
 * - Per-item "notes" field.
 * - isInList(variantId) helper for "In Quote List" card/product-page state.
 * - Shared toast() helper.
 * - renderCount() updates count text and aria-label.
 * - Header count is explicitly DISTINCT PRODUCTS (list.length).
 */
(function () {
  var STORAGE_KEY = 'aartech_quote_list_v2';
  var OLD_STORAGE_KEY = 'aartech_quote_list_v1';
  var SCHEMA_VERSION = 2;
  var MAX_QTY_PIECE = 10000;
  var MAX_QTY_KG = 10000;
  var MAX_NOTE_LENGTH = 500;
  var ALLOWED_UNITS = ['piece', 'kg'];

  function isFinitePositive(n) {
    return typeof n === 'number' && isFinite(n) && n > 0;
  }

  function sanitizeUnit(unit) {
    return ALLOWED_UNITS.indexOf(unit) !== -1 ? unit : 'piece';
  }

  function sanitizeQty(qty, unit) {
    var n = Number(qty);
    if (!isFinitePositive(n)) n = 1;
    if (unit === 'kg') {
      n = Math.min(MAX_QTY_KG, Math.round(n * 100) / 100);
      if (n < 0.1) n = 0.1;
    } else {
      n = Math.min(MAX_QTY_PIECE, Math.round(n));
      if (n < 1) n = 1;
    }
    return n;
  }

  function sanitizeItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var variantId = raw.variantId != null ? String(raw.variantId) : '';
    if (!variantId) return null;
    var unit = sanitizeUnit(raw.unit);
    return {
      productId: raw.productId != null ? String(raw.productId) : '',
      variantId: variantId,
      title: typeof raw.title === 'string' ? raw.title.slice(0, 300) : '',
      sku: typeof raw.sku === 'string' ? raw.sku.slice(0, 100) : '',
      variantTitle: typeof raw.variantTitle === 'string' ? raw.variantTitle.slice(0, 200) : '',
      unit: unit,
      qty: sanitizeQty(raw.qty, unit),
      url: typeof raw.url === 'string' ? raw.url.slice(0, 500) : '',
      image: typeof raw.image === 'string' ? raw.image.slice(0, 500) : '',
      model: typeof raw.model === 'string' ? raw.model.slice(0, 100) : '',
      notes: typeof raw.notes === 'string' ? raw.notes.slice(0, MAX_NOTE_LENGTH) : ''
    };
  }

  function migrateFromV1() {
    try {
      var raw = window.localStorage.getItem(OLD_STORAGE_KEY);
      if (!raw) return [];
      var oldList = JSON.parse(raw);
      if (!Array.isArray(oldList)) return [];
      var migrated = oldList.map(sanitizeItem).filter(Boolean);
      window.localStorage.removeItem(OLD_STORAGE_KEY);
      return migrated;
    } catch (e) {
      return [];
    }
  }

  function readList() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        var migrated = migrateFromV1();
        if (migrated.length) writeList(migrated, true);
        return migrated;
      }
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) {
        return [];
      }
      return parsed.items.map(sanitizeItem).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function writeList(list, skipEvent) {
    var clean = list.map(sanitizeItem).filter(Boolean);
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ version: SCHEMA_VERSION, items: clean })
      );
    } catch (e) {
      /* storage unavailable - fail silently */
    }
    renderCount();
    if (!skipEvent) {
      document.dispatchEvent(new CustomEvent('aartech:quote-list-changed', { detail: { list: clean } }));
    }
  }

  function addItem(item) {
    var clean = sanitizeItem(item);
    if (!clean) return;
    var list = readList();
    var existing = list.find(function (i) {
      return i.variantId === clean.variantId;
    });
    if (existing) {
      existing.qty = sanitizeQty(existing.qty + clean.qty, existing.unit);
    } else {
      list.push(clean);
    }
    writeList(list);
  }

  function removeItem(variantId) {
    var id = String(variantId);
    var list = readList().filter(function (i) {
      return i.variantId !== id;
    });
    writeList(list);
  }

  function updateQty(variantId, qty) {
    var id = String(variantId);
    var list = readList();
    var item = list.find(function (i) {
      return i.variantId === id;
    });
    if (!item) return;
    item.qty = sanitizeQty(qty, item.unit);
    writeList(list);
  }

  function incrementQty(variantId, delta) {
    var id = String(variantId);
    var list = readList();
    var item = list.find(function (i) {
      return i.variantId === id;
    });
    if (!item) return;
    var step = item.unit === 'kg' ? 0.1 : 1;
    item.qty = sanitizeQty(item.qty + delta * step, item.unit);
    writeList(list);
  }

  function updateUnit(variantId, unit) {
    var id = String(variantId);
    var list = readList();
    var item = list.find(function (i) {
      return i.variantId === id;
    });
    if (!item) return;
    item.unit = sanitizeUnit(unit);
    item.qty = sanitizeQty(item.qty, item.unit);
    writeList(list);
  }

  function updateNotes(variantId, notes) {
    var id = String(variantId);
    var list = readList();
    var item = list.find(function (i) {
      return i.variantId === id;
    });
    if (!item) return;
    item.notes = typeof notes === 'string' ? notes.slice(0, MAX_NOTE_LENGTH) : '';
    writeList(list);
  }

  function clearList() {
    writeList([]);
  }

  function itemCount() {
    // Distinct products, not summed quantity - by design.
    return readList().length;
  }

  function isInList(variantId) {
    var id = String(variantId);
    return readList().some(function (i) {
      return i.variantId === id;
    });
  }

  function renderCount() {
    var count = itemCount();
    document.querySelectorAll('[data-aartech-quote-list-count]').forEach(function (el) {
      el.textContent = String(count);
    });
    document.querySelectorAll('[data-aartech-quote-list-count-text]').forEach(function (el) {
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-aartech-quote-list-btn]').forEach(function (el) {
      var template = el.getAttribute('data-aria-template') || 'Open Quote List with {count} products';
      el.setAttribute('aria-label', template.replace('{count}', String(count)));
    });
  }

  function formatListAsText() {
    var list = readList();
    if (list.length === 0) return '';
    return list
      .map(function (i) {
        var unitLabel = i.unit === 'kg' ? 'kg' : 'pc' + (i.qty === 1 ? '' : 's');
        var variantPart = i.variantTitle && i.variantTitle !== 'Default Title' ? ' (' + i.variantTitle + ')' : '';
        var skuPart = i.sku ? ' — SKU: ' + i.sku : '';
        var notesPart = i.notes ? ' — Note: ' + i.notes : '';
        return '• ' + i.title + variantPart + skuPart + ' — Qty: ' + i.qty + ' ' + unitLabel + notesPart;
      })
      .join('\n');
  }

  function mergeFromCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var list = readList();
        (cart.items || []).forEach(function (line) {
          var variantId = String(line.variant_id);
          var existing = list.find(function (i) {
            return i.variantId === variantId;
          });
          var qty = Number(line.quantity) || 1;
          if (existing) {
            existing.qty = sanitizeQty(existing.qty + qty, existing.unit);
          } else {
            list.push(
              sanitizeItem({
                productId: line.product_id,
                variantId: variantId,
                title: line.product_title,
                sku: line.sku || '',
                variantTitle: line.variant_title || '',
                unit: 'piece',
                qty: qty,
                url: line.url || '',
                image: line.image || ''
              })
            );
          }
        });
        writeList(list);
        return list;
      });
  }

  // Shared toast: "Added to Quote List" with View Quote List / Continue
  // Shopping actions. Auto-hides after 5s, dismissible, aria-live polite.
  var toastTimer = null;
  function toast(message, viewLabel, continueLabel, quoteListUrl) {
    var existing = document.getElementById('aartech-toast');
    if (existing) existing.remove();
    if (toastTimer) window.clearTimeout(toastTimer);

    var el = document.createElement('div');
    el.id = 'aartech-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.className = 'aartech-toast';

    var text = document.createElement('span');
    text.textContent = message;
    el.appendChild(text);

    var actions = document.createElement('div');
    actions.className = 'aartech-toast__actions';

    if (viewLabel && quoteListUrl) {
      var viewLink = document.createElement('a');
      viewLink.href = quoteListUrl;
      viewLink.textContent = viewLabel;
      viewLink.className = 'aartech-toast__action';
      actions.appendChild(viewLink);
    }
    if (continueLabel) {
      var dismissBtn = document.createElement('button');
      dismissBtn.type = 'button';
      dismissBtn.textContent = continueLabel;
      dismissBtn.className = 'aartech-toast__action aartech-toast__action--secondary';
      dismissBtn.addEventListener('click', function () {
        el.remove();
      });
      actions.appendChild(dismissBtn);
    }
    el.appendChild(actions);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'aartech-toast__close';
    closeBtn.setAttribute('aria-label', 'Dismiss');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', function () {
      el.remove();
    });
    el.appendChild(closeBtn);

    document.body.appendChild(el);
    toastTimer = window.setTimeout(function () {
      if (el.parentNode) el.remove();
    }, 5000);
  }

  window.AartechQuoteList = {
    read: readList,
    add: addItem,
    remove: removeItem,
    updateQty: updateQty,
    incrementQty: incrementQty,
    updateUnit: updateUnit,
    updateNotes: updateNotes,
    clear: clearList,
    count: itemCount,
    isInList: isInList,
    formatAsText: formatListAsText,
    mergeFromCart: mergeFromCart,
    toast: toast,
    ALLOWED_UNITS: ALLOWED_UNITS
  };

  // CRITICAL FIX: announce readiness so consumers that parse/run before
  // this deferred script executes (e.g. inline scripts in body sections)
  // can wait for this event instead of silently giving up.
  document.dispatchEvent(new CustomEvent('aartech:quote-list-ready'));

  document.addEventListener('DOMContentLoaded', renderCount);
})();
